import { z } from 'zod';

import { CursorPaginationMetaSchema, PaginationSchema } from '../../pagination.js';

export const PurchaseOrderStatusSchema = z.enum([
  'DRAFT',
  'SENT',
  'CONFIRMED',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CANCELLED',
]);

export const PurchaseOrderStatusFilterSchema = z.enum([
  'all',
  'DRAFT',
  'SENT',
  'CONFIRMED',
  'PARTIALLY_RECEIVED',
  'RECEIVED',
  'CANCELLED',
]);

export const ListPurchaseOrdersParamsSchema = z.object({
  placeId: z.uuid(),
});

export const ListPurchaseOrdersQuerySchema = PaginationSchema.extend({
  status: PurchaseOrderStatusFilterSchema.optional().default('all'),
  supplierRoleId: z.uuid().optional(),
});

export type ListPurchaseOrdersQuery = z.infer<typeof ListPurchaseOrdersQuerySchema>;

export const PurchaseOrderListItemSchema = z.object({
  id: z.uuid(),
  displayNumber: z.string().min(1),
  placeId: z.uuid(),
  supplierRoleId: z.uuid().nullable(),
  purchaseQuoteId: z.uuid().nullable(),
  status: PurchaseOrderStatusSchema,
  itemCount: z.number().int().nonnegative(),
  totalCents: z.string(),
  currency: z.string(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type PurchaseOrderListItem = z.infer<typeof PurchaseOrderListItemSchema>;
export type PurchaseOrderStatus = z.infer<typeof PurchaseOrderStatusSchema>;
export type PurchaseOrderStatusFilter = z.infer<typeof PurchaseOrderStatusFilterSchema>;

export const ListPurchaseOrdersResponseSchema = z.object({
  items: z.array(PurchaseOrderListItemSchema),
  pagination: CursorPaginationMetaSchema,
});

export type ListPurchaseOrdersResponse = z.infer<typeof ListPurchaseOrdersResponseSchema>;

export const PurchaseOrderIdParamsSchema = z.object({
  id: z.uuid(),
});

export const PurchaseOrderLineResponseSchema = z.object({
  id: z.uuid(),
  placeProductId: z.uuid(),
  serviceQuoteItemId: z.uuid().nullable(),
  productName: z.string(),
  productSku: z.string().nullable(),
  brand: z.string().nullable(),
  measurementUnitCode: z.string().nullable(),
  quantityOrdered: z.string(),
  quantityReceived: z.string(),
  unitPriceCents: z.string(),
  currency: z.string(),
  physicalStockEntryQuantity: z.string(),
});

export const PurchaseOrderSupplierResponseSchema = z.object({
  name: z.string(),
  type: z.string().nullable(),
  phone: z.string().nullable(),
  paymentTermsLabel: z.string().nullable(),
  documentNumber: z.string().nullable(),
});

export const PurchaseOrderResponseSchema = z.object({
  id: z.uuid(),
  displayNumber: z.string().min(1),
  placeId: z.uuid(),
  supplierRoleId: z.uuid().nullable(),
  purchaseQuoteId: z.uuid().nullable(),
  serviceOrderId: z.uuid().nullable(),
  placeDocumentNumber: z.string().nullable(),
  placeName: z.string().nullable(),
  placeStateRegistration: z.string().nullable(),
  supplier: PurchaseOrderSupplierResponseSchema.nullable(),
  status: PurchaseOrderStatusSchema,
  currency: z.string(),
  subtotalCents: z.string(),
  freightCents: z.string(),
  totalCents: z.string(),
  notes: z.string().nullable(),
  expectedDeliveryAt: z.iso.datetime().nullable(),
  sentAt: z.iso.datetime().nullable(),
  confirmedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  defaultPlaceStorageId: z.uuid().nullable(),
  canPhysicalReceipt: z.boolean(),
  lines: z.array(PurchaseOrderLineResponseSchema),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type PurchaseOrderResponse = z.infer<typeof PurchaseOrderResponseSchema>;

export const ReceivePurchaseOrderLineBodySchema = z.object({
  purchaseOrderItemId: z.uuid(),
  quantity: z.string().regex(/^\d+(\.\d{1,3})?$/),
  placeStorageId: z.uuid().optional(),
});

export const ReceivePurchaseOrderBodySchema = z.object({
  postPhysicalStockEntry: z.boolean().optional().default(false), // BR-PUR-34: default off until NF↔PO linkage is explicit
  lines: z.array(ReceivePurchaseOrderLineBodySchema).min(1),
});

export type ReceivePurchaseOrderBody = z.infer<typeof ReceivePurchaseOrderBodySchema>;

export const ReceivePurchaseOrderLineResponseSchema = z.object({
  purchaseOrderItemId: z.uuid(),
});

export const SkippedPhysicalEntryReasonSchema = z.enum([
  'NOT_STOCK_CONTROLLED',
  'PRODUCT_NOT_ENABLED',
  'PLACE_PRODUCT_NOT_FOUND',
  'PHYSICAL_ENTRY_ALREADY_DONE',
]);

export const SkippedPhysicalEntryLineResponseSchema = z.object({
  purchaseOrderItemId: z.uuid(),
  reason: SkippedPhysicalEntryReasonSchema,
});

export const ReceivePurchaseOrderResponseSchema = z.object({
  purchaseOrderId: z.uuid(),
  status: PurchaseOrderStatusSchema,
  lines: z.array(ReceivePurchaseOrderLineResponseSchema),
  skippedPhysicalEntryLines: z.array(SkippedPhysicalEntryLineResponseSchema).optional(),
});

export type ReceivePurchaseOrderResponse = z.infer<typeof ReceivePurchaseOrderResponseSchema>;

export const PurchaseOrderActionResponseSchema = z.object({
  purchaseOrderId: z.uuid(),
  status: PurchaseOrderStatusSchema,
});

export type PurchaseOrderActionResponse = z.infer<typeof PurchaseOrderActionResponseSchema>;

/** BR-PUR-13 — required by domain when PO lines have OS/quote origin. */
export const CancelPurchaseOrderBodySchema = z.object({
  reason: z.string().trim().min(1).optional(),
});

export type CancelPurchaseOrderBody = z.infer<typeof CancelPurchaseOrderBodySchema>;
